/**
 * Follow-ups data layer — quoted properties and where each one sits in the follow-up pipeline.
 *
 * Test data today; later it comes from Zoho through a server function:
 *   follow-up + stage   → the Deal (quote sent) and its follow-up stage / next call date
 *   quotes              → Zoho Quotes on the Deal (the app's quote builder creates them)
 *   photos              → the inspection photos
 *   notes               → Notes on the Deal
 * The Calls pages only use what's exported here.
 *
 * Stages, in pipeline order:
 *   scheduled  Follow-up Scheduled — a call date is set
 *   call_now   Call Now            — due today or overdue (a scheduled follow-up moves here on its date)
 *   booked     Job Booked  — with the package that sold and its total (the dashboard's "Sold")
 *   dismissed  Dismissed
 */
import { useSyncExternalStore } from 'react';
import mockFollowUps from '../mockData/mockFollowUps';
import { queueChange } from './sync';

export const FOLLOW_UP_STAGES = [
  { id: 'scheduled', label: 'Follow-up Scheduled' },
  { id: 'call_now', label: 'Call Now' },
  { id: 'booked', label: 'Job Booked' },
  { id: 'dismissed', label: 'Dismissed' },
];

export const HST_RATE = 0.13;
const DAY = 24 * 60 * 60 * 1000;

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/* ---------- quote maths ---------- */
export const lineAmount = (l) => Math.round(l.qty * l.rate * 100) / 100;
export const quoteSubtotal = (q) => q.lines.reduce((s, l) => s + lineAmount(l), 0);
// same rule as the quote pricing page: the discount comes off the subtotal, HST goes on what's left
export const quoteDiscount = (q) => {
  const d = q.discount;
  if (!d) return 0;
  const sub = quoteSubtotal(q);
  return Math.round((d.type === 'percent' ? (sub * (d.value || 0)) / 100 : d.value || 0) * 100) / 100;
};
export const quoteHst = (q) => Math.round((quoteSubtotal(q) - quoteDiscount(q)) * HST_RATE * 100) / 100;
export const quoteTotal = (q) => Math.round((quoteSubtotal(q) - quoteDiscount(q) + quoteHst(q)) * 100) / 100;
export const highestQuote = (f) => Math.max(0, ...(f.quotes || []).map(quoteTotal));
// what the customer bought — set when the job is booked
export const soldValue = (f) => (f.booked ? f.booked.total : 0);
const soldFrom = (q) => ({ number: q.number || '', name: q.name, total: quoteTotal(q) });

/* ---------- store ---------- */
// test data is written relative to today so the 30d / 60d / 90d filters always have something in them
const toRecord = (f, today) => ({
  ...f,
  quotedAt: new Date(today.getTime() - f.quotedDaysAgo * DAY).toISOString(),
  callDate: f.callInDays == null ? null : new Date(startOfDay(today).getTime() + f.callInDays * DAY).toISOString(),
  bookedAt: f.bookedDaysAgo == null ? null : new Date(today.getTime() - f.bookedDaysAgo * DAY).toISOString(),
  booked: f.stage === 'booked' && f.quotes[f.bookedQuote || 0] ? soldFrom(f.quotes[f.bookedQuote || 0]) : null,
  notes: (f.notes || []).map((n, i) => ({
    id: `${f.id}-n${i}`,
    author: n.author,
    text: n.text,
    at: new Date(today.getTime() - n.daysAgo * DAY).toISOString(),
  })),
});

const now = new Date();
let followUps = mockFollowUps.map((f) => toRecord(f, now));
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const getFollowUps = () => followUps;
export const useFollowUps = () => useSyncExternalStore(subscribe, getFollowUps);
export const useFollowUp = (id) => useFollowUps().find((f) => f.id === id) || null;

/**
 * The stage a follow-up shows under: a scheduled call whose date has arrived is a Call Now.
 */
export const effectiveStage = (f, today = new Date()) => {
  if (f.stage !== 'scheduled' || !f.callDate) return f.stage;
  const endOfToday = startOfDay(today).getTime() + DAY - 1;
  return new Date(f.callDate).getTime() <= endOfToday ? 'call_now' : 'scheduled';
};

// apply the change here, then queue it for Zoho
const update = (id, fn, zoho) => {
  followUps = followUps.map((f) => (f.id === id ? fn(f) : f));
  emit();
  const f = followUps.find((x) => x.id === id);
  if (zoho) queueChange({ type: zoho.type, leadId: id, label: `${f.name} — ${zoho.label}`, payload: zoho.payload || {} });
  return f;
};

const noteEntry = (text, author = 'Ibrahim') => ({ id: `n-${Date.now()}`, author, text, at: new Date().toISOString() });

/* ---------- actions ---------- */
export const addNote = async (id, text) => {
  if (!text.trim()) throw new Error('Write a note first');
  return update(id, (f) => ({ ...f, notes: [...f.notes, noteEntry(text.trim())] }), {
    type: 'followup.note', label: 'note added', payload: { note: text.trim() },
  });
};

// the rep picks which package the customer bought — that's what counts as sold
export const bookJob = async (id, quoteIdx = 0) => {
  const current = followUps.find((x) => x.id === id);
  const q = current && current.quotes[quoteIdx];
  if (!q) throw new Error('Pick the package they booked');
  const sold = soldFrom(q);
  const amount = `$${sold.total.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return update(
    id,
    (f) => ({
      ...f,
      stage: 'booked',
      callDate: null,
      booked: sold,
      bookedAt: new Date().toISOString(),
      bookingRequest: null,
      notes: [...f.notes, noteEntry(`Job booked: ${sold.name} (${amount}).`)],
    }),
    { type: 'followup.book', label: `job booked — ${sold.name} ${amount}`, payload: { stage: 'Job Booked', quote: sold } }
  );
};

// a reason is required, like a cancelled appointment
export const dismissFollowUp = async (id, reason) => {
  if (!reason.trim()) throw new Error('Add a reason');
  return update(id, (f) => ({
    ...f,
    stage: 'dismissed',
    callDate: null,
    notes: [...f.notes, noteEntry(`Dismissed: ${reason.trim()}`)],
  }), { type: 'followup.dismiss', label: 'dismissed', payload: { stage: 'Dismissed', note: reason.trim() } });
};

// set the next call date — back to Follow-up Scheduled (Call Now on the day)
export const scheduleFollowUp = async (id, dateISO, note = '') => {
  if (!dateISO) throw new Error('Pick a date');
  const callDate = new Date(`${dateISO}T09:00:00`).toISOString();
  const label = new Date(callDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return update(id, (f) => ({
    ...f,
    stage: 'scheduled',
    callDate,
    notes: [...f.notes, noteEntry(`Follow-up scheduled for ${label}.${note.trim() ? ` ${note.trim()}` : ''}`)],
  }), { type: 'followup.schedule', label: `follow-up ${label}`, payload: { callDate, note: note.trim() } });
};

export const logNoAnswer = async (id) =>
  update(id, (f) => ({ ...f, notes: [...f.notes, noteEntry('Called — no answer.')] }), {
    type: 'followup.noAnswer', label: 'called, no answer',
  });

export const listFollowUps = async () => followUps;

/* ---------- from the inspection flow ---------- */

const DEFAULT_CALL_IN_DAYS = 2;

/**
 * A report + quotes were sent: the customer becomes a follow-up (Follow-up Scheduled, call in 2 days)
 * with the quotes exactly as built, the inspection photos and the inspector's notes.
 */
export const addFollowUpFromReport = ({ leadId, name, address, phone, email, homeType, packages, photos, notes }) => {
  const today = new Date();
  const record = {
    id: `fu-${Date.now()}`,
    leadId: leadId || null,
    name: name || 'Customer',
    address: address || '',
    phone: phone || '',
    email: email || '',
    phoneType: 'cell',
    homeType: homeType || '',
    stage: 'scheduled',
    quotedAt: today.toISOString(),
    callDate: new Date(startOfDay(today).getTime() + DEFAULT_CALL_IN_DAYS * DAY).toISOString(),
    quotes: (packages || []).map((pkg, i) => ({
      number: `QT-${String(Date.now()).slice(-6)}-${i + 1}`,
      name: pkg.name,
      discount: pkg.discount || null,
      lines: (pkg.services || []).map((sv) => ({
        category: sv.category || 'Services',
        name: sv.name,
        description: sv.description || '',
        qty: sv.quantity || 1,
        unit: String(sv.unit || 'ea').toLowerCase(),
        rate: sv.unitRate || 0,
      })),
    })),
    photos: photos || [],
    notes: (notes || []).filter(Boolean).map((text, i) => ({ id: `fu-${Date.now()}-n${i}`, author: 'Inspection', text, at: today.toISOString() })),
  };
  followUps = [record, ...followUps];
  emit();
  queueChange({
    type: 'followup.create',
    leadId: leadId || null,
    label: `${record.name} — follow-up scheduled`,
    payload: { callDate: record.callDate, quotes: record.quotes.length },
  });
  return record;
};

/* ---------- from the customer's report ---------- */

/**
 * The customer tapped "Book your job now" on their report: their follow-up jumps to Call Now
 * (due today) with the request in its notes, so the office calls them first.
 * Matched by lead id, else by name + address. Returns the follow-up, or null if none matches
 * (the request still reaches Zoho through the sync queue).
 */
export const requestCallBack = ({ leadId, name, address, text, quoteName }) => {
  const match = followUps.find(
    (f) => (leadId && f.leadId === leadId) || (!leadId && f.name === name && (!address || f.address.startsWith(address)))
  );
  if (!match) return null;
  return update(match.id, (f) => ({
    ...f,
    stage: 'call_now',
    callDate: new Date().toISOString(),
    // shows on the dashboard until the job is booked; pre-picks their package on Book job
    bookingRequest: { at: new Date().toISOString(), quoteName: quoteName || '' },
    notes: [...f.notes, { ...noteEntry(text, 'Customer') }],
  }));
};
